import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Pie, Line, Bar } from "react-chartjs-2";
import { message } from "antd";
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
    BarElement,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    ChartData,
    ChartOptions
} from "chart.js";
import { baseUrl } from "../../../constants/api";
import { API_ENDPOINTS } from "../../../constants/endpoints";
import { useDispatch, useSelector } from "react-redux";
import { setTopics, RootState } from "../../../store";
import generatePrintableReport from "../../../utils/generatePrintableReport";
import html2canvas from "html2canvas";
import DashboardCharts, { getSubtopicColor } from "../../../components/Shared/DashboardCharts";
import BackBtn from '../../../assets/svg/backbtn.svg';
import StatsOverview from "./StatsOverview";
import { ReportModal } from "../../../components/Modal/ReportModal";


import TestScoreIcon from '../../../assets/images/test analytics icon.png';
import AverageScoreIcon from '../../../assets/images/test summary icon.png';
import infoIcon from '../../../assets/svg/info.svg';

ChartJS.register(
    ArcElement,
    Tooltip,
    Legend,
    BarElement,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement
);

interface Topic {
    topic_name: string;
    subtopics: string[];
}

interface Participant {
    id: string;
    name: string;
    designation: string;
}

interface CertificationDashboardRow {
    id: string;
    serial_no: number;
    attempt_number: number;
    date_of_test: string;
    candidate_name: string;
    designation_hq: string;
    cli_name: string | null;
    selected_cli_name: string | null;
    assigned_cli_name: string | null;
    score_percentage: number;
    certificate_no: string;
    certificate_available: boolean;
    due_date: string | null;
    is_passed: boolean;
}

interface CertificationDashboardSection {
    quiz_id: string;
    quiz_name: string;
    topic_name: string;
    subtopic_name: string;
    due_date: string | null;
    passing_percentage: number;
    rows: CertificationDashboardRow[];
}

const MEDAL: Record<number, string> = { 1: "🥇", 2: "🥈", 3: "🥉" };

const getCertificationGradient = (context: any, startColor: string, endColor: string) => {
    const chart = context.chart;
    const { ctx, chartArea } = chart;

    if (!chartArea) {
        return startColor;
    }

    const gradient = ctx.createLinearGradient(chartArea.left, chartArea.top, chartArea.right, chartArea.bottom);
    gradient.addColorStop(0, startColor);
    gradient.addColorStop(1, endColor);
    return gradient;
};

const AdminDashboard = () => {
    const dispatch = useDispatch();
    const [dashboardView, setDashboardView] = useState<"regular" | "certification">("regular");
    const [selectedTopic, setSelectedTopic] = useState<string | null>(null);
    const [selectedSubtopic, setSelectedSubtopic] = useState<string | null>(null);
    const [certificationSections, setCertificationSections] = useState<CertificationDashboardSection[]>([]);

    const [pieData, setPieData] = useState<ChartData<'pie'>>({
        labels: [],
        datasets: [
            {
                data: [],
                backgroundColor: ["#002E6E", "#FFAC40"],
                borderWidth: 1,
            },
        ],
    });
    const [userPieData, setUserPieData] = useState<ChartData<'pie'>>({
        labels: [],
        datasets: [
            {
                data: [],
                backgroundColor: ["#002E6E", "#FFAC40"],
                borderWidth: 0,
            },
        ],
    });

    const [lowUserData, setLowUserData] = useState<Participant[]>([]);
    const [highUserData, setHighUserData] = useState<Participant[]>([]);
    const [showAllUsers, setShowAllUsers] = useState(false);
    const [filter, setFilter] = useState("all");
    const token = useSelector((state: RootState) => state.account.token);
    const [pieChartCanvas, setPieChartCanvas] = useState("");
    const CurrentYear = new Date().getFullYear();
    const [selectedYear] = useState(CurrentYear);
    const { topics } = useSelector((state: RootState) => state.quiz);
    const [, setFullYearData] = useState<ChartData<'bar'> | null>(null);
    const [selectedParticipant, setSelectedParticipant] = useState<Participant | null>(null);
    const [userAnalytics, setUserAnalytics] = useState<any>(null);
    const [marksData, setMarksData] = useState<ChartData<'line'> | null>(null);
    const [isReportModalOpen, setIsReportModalOpen] = useState(false);

    const { data: overviewData = { users: 0, quizzes: 0, questions: 0 } } = useQuery({
        queryKey: ['dashboardOverview'],
        queryFn: async () => {
            const response = await fetch(`${baseUrl}${API_ENDPOINTS.GET_DASHBOARD_OVERVIEW}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                if (data.success) return data.data;
            }
            return { users: 0, quizzes: 0, questions: 0 };
        },
        enabled: !!token,
    });

    // Charts have been refactored into <DashboardCharts /> component
    // Charts have been refactored into <DashboardCharts /> component

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    const handleTopicSelect = (topic: string | null) => {
        // Always keep a real topic selected — fallback to the first topic
        setSelectedTopic(topic || (topics.length > 0 ? topics[0].topic_name : null));
        setSelectedSubtopic(null);
    };

    const handleSubtopicSelect = (subtopic: string | null) => {
        setSelectedSubtopic(subtopic);
    };

    const handlePieChartImage = () => {
        if (userPieData) {
            const pieChartContainer = document.getElementById(
                "pie-chart-container"
            );
            if (pieChartContainer) {
                html2canvas(pieChartContainer).then((canvas) => {
                    const pieChartImg = canvas.toDataURL("image/png");
                    setPieChartCanvas(pieChartImg);
                });
            }
        }
    };

    const filteredData =
        filter === "above50"
            ? highUserData
            : filter === "below50"
                ? lowUserData
                : [...highUserData, ...lowUserData];

    useQuery({
        queryKey: ['certificationDashboard', dashboardView],
        queryFn: async () => {
            const response = await fetch(`${baseUrl}${API_ENDPOINTS.GET_CERTIFICATION_LEADERBOARD}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setCertificationSections(data.data || []);
                return data.data || [];
            }
            return [];
        },
        enabled: !!token && dashboardView === 'certification',
    });

    useQuery({
        queryKey: ['topics'],
        queryFn: async () => {
            const apiUrl = `${baseUrl}${API_ENDPOINTS.GET_ALL_TOPICS}`;
            const response = await fetch(apiUrl, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
                const topicData = await response.json();
                dispatch(setTopics(topicData.data));
                return topicData.data as Topic[];
            } else {
                const errorText = await response.text();
                message.error(`Failed to fetch topics: ${errorText}`);
                return null;
            }
        },
        enabled: !!token,
    });

    useQuery({
        queryKey: ['adminAnalytics', selectedTopic, selectedSubtopic],
        queryFn: async () => {
            const apiUrl = `${baseUrl}${API_ENDPOINTS.GET_ADMIN_ANALYTICS}?topic_name=${selectedTopic}&subtopic_name=${selectedSubtopic}`;
            const response = await fetch(apiUrl, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
                const analyticsData = await response.json();
                if (analyticsData) {
                    const highScoringUsers = analyticsData.high_scoring_users.map((user: any) => ({
                        name: user.name,
                        designation: user.designation,
                        id: user._id,
                    }));

                    const lowScoringUsers = analyticsData.low_scoring_users.map((user: any) => ({
                        name: user.name,
                        designation: user.designation,
                        id: user._id,
                    }));
                    setLowUserData(lowScoringUsers);
                    setHighUserData(highScoringUsers);
                    const totalUsers = highScoringUsers.length + lowScoringUsers.length;
                    const high_percentage = totalUsers > 0 ? (highScoringUsers.length / totalUsers) * 100 : 0;
                    const low_percentage = totalUsers > 0 ? 100 - high_percentage : 0;
                    const newPieData: ChartData<'pie'> = {
                        labels: ["Marks above 50%", "Marks below 50%"],
                        datasets: [
                            {
                                data: [high_percentage, low_percentage],
                                backgroundColor: ["#002E6E", "#FFAC40"],
                                borderWidth: 1,
                            },
                        ],
                    };
                    setPieData(newPieData);
                }
                return analyticsData;
            } else {
                message.error(`Failed to fetch analytics`);
                return null;
            }
        },
        enabled: !!token && !!selectedTopic && !!selectedSubtopic,
    });

    useQuery({
        queryKey: ['quizChart', selectedTopic, selectedSubtopic, selectedYear],
        queryFn: async () => {
            const quizApiUrl = `${baseUrl}${API_ENDPOINTS.GET_QUIZ_CHART}?topic_name=${selectedTopic}&subtopic_name=${selectedSubtopic}&year=${selectedYear}`;
            const response = await fetch(quizApiUrl, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response?.status === 200) {
                const YearlyQuizesData = await response.json();
                const monthlyData = YearlyQuizesData?.["quiz_analytics"] || [];

                const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

                const fullData: ChartData<'bar'> = {
                    labels: monthNames,
                    datasets: [
                        {
                            data: Array(12).fill(0),
                            backgroundColor: ["#2A2A2A", "#9D9FA2"],
                            borderWidth: 1,
                            borderRadius: 10,
                        },
                    ],
                };

                monthlyData.forEach((quiz: any) => {
                    if (fullData.datasets && fullData.datasets[0].data) {
                        (fullData.datasets[0].data as number[])[quiz.month - 1] = quiz.totalQuizes || 0;
                    }
                });

                setFullYearData(fullData);
                return fullData;
            } else {
                const emptyData: ChartData<'bar'> = {
                    labels: [],
                    datasets: [{ data: [], backgroundColor: ["#2A2A2A", "#9D9FA2"], borderWidth: 1, borderRadius: 10 }],
                };
                setFullYearData(emptyData);
                return null;
            }
        },
        enabled: !!token && !!selectedTopic && !!selectedSubtopic && !!selectedYear,
    });

    useEffect(() => {
        if (topics && topics.length > 0 && !selectedTopic) {
            setSelectedTopic(topics[0].topic_name);
            if (topics[0].subtopics && topics[0].subtopics.length > 0) {
                setSelectedSubtopic(topics[0].subtopics[0]);
            }
        }
    }, [topics, selectedTopic]);

    const PieOptions: ChartOptions<'pie'> = {
        responsive: true,
        plugins: {
            tooltip: {
                enabled: true,
            },
            legend: {
                display: false,
            },
        },
    };

    const options: ChartOptions<'pie'> = {
        responsive: true,
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                callbacks: {
                    label: (tooltipItem) => {
                        const value = tooltipItem.raw;
                        const label = tooltipItem.label;
                        return `${label}: ${value}`;
                    },
                },
            },
        },
        cutout: '50%',
        radius: '90%',
    };

    const { isFetching: isUserAnalyticsLoading } = useQuery({
        queryKey: ['userAnalytics', selectedParticipant?.id],
        queryFn: async () => {
            if (!selectedParticipant) return null;
            const analyticsResponse = await fetch(`${baseUrl}${API_ENDPOINTS.QUIZ_REPORTS}?user_id=${selectedParticipant.id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (analyticsResponse.ok) {
                const analyticsData = await analyticsResponse.json();
                setUserAnalytics(analyticsData?.report);
                const tests = analyticsData?.report?.per_test || [];

                if (tests.length > 0) {
                    const sortedTests = [...tests].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

                    const labels = sortedTests?.map((_, index) => `Quiz ${index + 1}`);
                    // Use percentage for the line chart instead of raw score
                    const percentages = sortedTests?.map((test) => test.percentage ?? (test.total_questions > 0 ? (test.score / test.total_questions) * 100 : 0));
                    const pointColors = percentages?.map((pct) => (pct < 50 ? '#D13636' : '#9DBD52'));

                    setMarksData({
                        labels,
                        datasets: [
                            {
                                label: 'Score (%)',
                                data: percentages,
                                fill: false,
                                borderColor: '#dee2e6',
                                backgroundColor: '#9DBD52',
                                tension: 0.4,
                                pointStyle: 'circle',
                                pointRadius: 6,
                                pointHoverRadius: 8,
                                pointBackgroundColor: pointColors,
                            },
                        ],
                    });

                    // Sum up actual correct/incorrect across all tests
                    const totalCorrect = sortedTests.reduce((sum, t) => sum + t.score, 0);
                    const totalQuestions = sortedTests.reduce((sum, t) => sum + (t.total_questions || 0), 0);
                    const totalIncorrect = totalQuestions - totalCorrect;

                    setUserPieData({
                        labels: ['Correct', 'Incorrect'],
                        datasets: [
                            {
                                data: [totalCorrect, Math.max(0, totalIncorrect)],
                                backgroundColor: ['#253B6E', '#FF9304'],
                                borderWidth: 0,
                            },
                        ],
                    });
                } else {
                    setMarksData({ labels: [], datasets: [] });
                    setUserPieData({ labels: [], datasets: [] });
                }
                return analyticsData;
            } else {
                console.error('Failed to fetch participant analytics');
                return null;
            }
        },
        enabled: !!token && !!selectedParticipant,
    });

    const handleUserData = (participant: Participant) => {
        setSelectedParticipant(participant);
        setShowAllUsers(false);
    };
    const handleUserBackBtn = () => {
        setSelectedParticipant(null);
        setShowAllUsers(true);
    };

    const certificationRows = certificationSections.flatMap((section) => section.rows || []);
    const certificationAttempts = certificationRows.length;
    const certificationPassed = certificationRows.filter((row) => row.is_passed).length;
    const certificationFailed = certificationAttempts - certificationPassed;
    const certificationPassRate = certificationAttempts > 0 ? Math.round((certificationPassed / certificationAttempts) * 100) : 0;
    const certificationAverageScore = certificationAttempts > 0
        ? Math.round(certificationRows.reduce((sum, row) => sum + (row.score_percentage || 0), 0) / certificationAttempts)
        : 0;
    const certificationPieData: ChartData<'pie'> = {
        labels: ['Passed', 'Failed'],
        datasets: [
            {
                data: [certificationPassed, certificationFailed],
                backgroundColor: ['#0f766e', '#f97316'],
                borderColor: '#ffffff',
                borderWidth: 2,
                hoverOffset: 10,
            },
        ],
    };
    const certificationBarData: ChartData<'bar'> = {
        labels: certificationSections.map((section) => section.quiz_name.length > 18 ? `${section.quiz_name.slice(0, 18)}…` : section.quiz_name),
        datasets: [
            {
                label: 'Average Result %',
                data: certificationSections.map((section) => {
                    const sectionRows = section.rows || [];
                    if (sectionRows.length === 0) return 0;
                    const avg = sectionRows.reduce((sum, row) => sum + (row.score_percentage || 0), 0) / sectionRows.length;
                    return Math.round(avg);
                }),
                backgroundColor: (context) => getCertificationGradient(context, '#60a5fa', '#7c3aed'),
                borderColor: '#4338ca',
                borderWidth: 1,
                borderRadius: 14,
                hoverBackgroundColor: (context) => getCertificationGradient(context, '#22c55e', '#2563eb'),
            },
        ],
    };

    return (
        <div className="admin-analytics-page w-full h-full bg-transparent">
            <div className="user-specific-analytics"
                style={{ display: selectedParticipant ? "flex" : "none" }}
            >
                <div
                    className="back-btn user-analytics-back-btn"
                    onClick={() => handleUserBackBtn()}
                >
                    <img src={BackBtn} alt="back" />
                </div>
                <div className="analytics-page" style={{ minHeight: '100vh', paddingTop: '70px' }}>
                    <div className="left-section-ap">
                        <div className="test-analtics">
                            <div className="total-test">
                                <div className="analytics-top">
                                    <div className="total-test-heading">
                                        <div className="anaytics-gray-heading">Total Tests Given</div>
                                        <div className="test-score">{userAnalytics?.total_tests || '--'}</div>
                                    </div>
                                    <div className="analytics-logo">
                                        <img src={TestScoreIcon} alt="Test Score Icon" />
                                    </div>
                                </div>
                                <div className="test-summary">
                                    <span className="processed-analytics">
                                        {userAnalytics?.time_elapsed_since_last_quiz} days
                                    </span>{' '}
                                    since last test appeared
                                </div>
                            </div>
                            <div className="avg-mark">
                                <div className="analytics-top">
                                    <div className="avg-mark-heading">
                                        <div className="anaytics-gray-heading">Average Score</div>
                                        <div className="test-score">{userAnalytics?.average_score ? `${userAnalytics.average_score}%` : '--'}</div>
                                    </div>
                                    <div className="analytics-logo">
                                        <img src={AverageScoreIcon} alt="Average Score Icon" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="grid-analytics">
                            <div className="grid-heading">{selectedParticipant?.name}'s Analytics</div>
                            <div className="chart-container">
                                {isUserAnalyticsLoading ? (
                                    <div className="d-flex justify-content-center align-items-center w-100 h-100" style={{ minHeight: "200px" }}>
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                ) : marksData ? <Line data={marksData} options={{
                                    responsive: true,
                                    scales: {
                                        x: {
                                            ticks: {
                                                maxRotation: 90,
                                                minRotation: 90,
                                                align: 'end',
                                                font: {
                                                    size: 13,
                                                },
                                            },
                                        },
                                        y: {
                                            min: 0,
                                            max: 100,
                                            title: { display: true, text: 'Score (%)' },
                                        },
                                    },
                                }} /> : 'No data available'}
                            </div>
                        </div>
                    </div>
                    <div className="right-section-ap">
                        <div className="calendar-container">
                            <h3 className="calendar-heading">Test Summary</h3>
                            {isUserAnalyticsLoading ? (
                                <div className="d-flex justify-content-center align-items-center w-100 h-100 p-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : userPieData && userPieData.labels && userPieData.labels.length > 0 ? (
                                <>
                                    <Pie data={userPieData} options={options} />
                                    <div className="pie-summary">
                                        <div className='pie-summary-item'>
                                            <div className="pie-marker" style={{ backgroundColor: '#253B6E' }}></div>
                                            <div style={{ display: 'flex' }}>Total Correct <span style={{ width: '190px' }}></span> {userPieData.datasets[0].data[0]}</div>
                                        </div>
                                        <div className='gray-line-horizontal'></div>
                                        <div className='pie-summary-item'>
                                            <div className="pie-marker" style={{ backgroundColor: '#FF9304' }}></div>
                                            <div style={{ display: 'flex' }}>Total Incorrect  <span style={{ width: '180px' }}></span> {userPieData.datasets[0].data[1]}</div>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <p>No data available to display the summary.</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div
                className="admin-analytics-container"
                style={{ display: showAllUsers ? "flex" : "none" }}
            >
                <div className="all-users-dashboard">
                    <div className="users-heading">
                        <div className="main-heading">
                            <div
                                className="back-btn"
                                onClick={() => setShowAllUsers(false)}
                            >
                                <img src={BackBtn} alt="back" />
                            </div>
                            <div>Participants</div>{" "}
                        </div>
                        <div className="selectore-btns">
                            <button
                                className="sel-btn"
                                style={
                                    filter === "all"
                                        ? { backgroundColor: "#FFFFFF" }
                                        : {}
                                }
                                onClick={() => setFilter("all")}
                            >
                                All
                            </button>
                            <button
                                className="sel-btn"
                                style={
                                    filter === "above50"
                                        ? { backgroundColor: "#FFFFFF" }
                                        : {}
                                }
                                onClick={() => setFilter("above50")}
                            >
                                Above 50%
                            </button>
                            <button
                                className="sel-btn"
                                style={
                                    filter === "below50"
                                        ? { backgroundColor: "#FFFFFF" }
                                        : {}
                                }
                                onClick={() => setFilter("below50")}
                            >
                                Below 50%
                            </button>
                            <button
                                className="download-btn"
                                onClick={() => setIsReportModalOpen(true)}
                            >
                                Download Report
                            </button>
                        </div>
                    </div>
                    <ReportModal open={isReportModalOpen} onOpenChange={setIsReportModalOpen} />
                    <div
                        className="tab-container"
                        style={{ paddingTop: "60px" }}
                    >
                        <div className="table-header">
                            <div className="t-heading1">Name</div>
                            <div className="t-heading">Designation</div>
                        </div>
                        <div
                            className="gray-line"
                            style={{
                                marginBottom: "10px",
                                marginTop: "10px",
                                width: "100%",
                            }}
                        ></div>
                        {filteredData?.map((participant, index) => (
                            <div
                                key={index}
                                className="participant-profile d-flex align-items-center p-1 rounded"
                                onClick={async () => { handleUserData(participant) }}
                            >
                                <div
                                    className="d-flex justify-content-center align-items-center p-2"
                                    style={{
                                        borderRadius: "50%",
                                        backgroundColor: "#E7F1FE",
                                        color: "#95B8E2",
                                        fontWeight: "bold",
                                        fontSize: "1.2rem",
                                        width: "40px",
                                        height: "40px"
                                    }}
                                >
                                    {participant.name
                                        .split(" ")
                                        .slice(0, 2)
                                        .map((word) => word[0])
                                        .join("")}

                                </div>
                                <div className="all-participants">
                                    <div className="participant-name">
                                        {participant.name}
                                    </div>{" "}
                                    <span>
                                        {participant.designation}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <div
                className="admin-analytics-container"
                style={{ display: showAllUsers || selectedParticipant ? "none" : "flex" }}
            >
                <div className="left-section-dashboard" style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                    <StatsOverview
                        stats={[
                            { label: "Users", value: overviewData.users },
                            { label: "Quizzes", value: overviewData.quizzes },
                            { label: "Questions", value: overviewData.questions },
                        ]}
                    />
                    <div className="dashboard-mode-switch">
                        <button
                            className={`dashboard-mode-btn ${dashboardView === "regular" ? "active" : ""}`}
                            onClick={() => setDashboardView("regular")}
                        >
                            Regular Quiz
                        </button>
                        <button
                            className={`dashboard-mode-btn ${dashboardView === "certification" ? "active" : ""}`}
                            onClick={() => setDashboardView("certification")}
                        >
                            Certification Quiz
                        </button>
                    </div>
                    {dashboardView === "regular" ? (
                    <>
                    <div className="main-topics d-flex gap-3 flex-wrap">
                        <button
                            className={`topic-btn ${!selectedTopic ? "active" : ""}`}
                            onClick={() => handleTopicSelect(null)}
                        >
                            All Topics
                        </button>
                        {topics.map((topic, index) => (
                            <button
                                key={index}
                                className={`topic-btn ${selectedTopic === topic.topic_name ? "active" : ""}`}
                                onClick={() => handleTopicSelect(topic.topic_name)}
                            >
                                <span
                                    className="status-dot"
                                    style={{ backgroundColor: getSubtopicColor(index) }}
                                ></span>
                                {topic?.topic_name}
                            </button>
                        ))}
                    </div>
                    <div className="dashboard-graphs-container p-4 border rounded shadow-sm bg-white m-0">
                        {selectedTopic && (
                            <div className="subtopic-toggles d-flex gap-3 mb-4 flex-wrap pb-3 border-bottom">
                                <button
                                    className={`topic-btn ${!selectedSubtopic ? "active" : ""}`}
                                    onClick={() => handleSubtopicSelect(null)}
                                >
                                    All Subtopics
                                </button>
                                {topics
                                    .find((topic) => topic?.topic_name === selectedTopic)
                                    ?.subtopics.map((subtopic: string, index: number) => (
                                        <button
                                            key={index}
                                            className={`topic-btn ${selectedSubtopic === subtopic ? "active" : ""}`}
                                            onClick={() => handleSubtopicSelect(subtopic)}
                                            style={{
                                                borderColor: selectedSubtopic === subtopic ? getSubtopicColor(index) : undefined,
                                                backgroundColor: selectedSubtopic === subtopic ? getSubtopicColor(index) : undefined,
                                            }}
                                        >
                                            <span
                                                className="status-dot"
                                                style={{ backgroundColor: selectedSubtopic === subtopic ? '#fff' : getSubtopicColor(index) }}
                                            ></span>
                                            {subtopic}
                                        </button>
                                    ))}
                            </div>
                        )}
                        <DashboardCharts
                            token={token}
                            topics={topics}
                            selectedTopic={selectedTopic}
                            selectedSubtopic={selectedSubtopic}
                        />
                    </div>
                    </>
                    ) : (
                    <div className="dashboard-graphs-container p-4 border rounded shadow-sm bg-white m-0">
                        <div className="dashboard-mode-header certification-mode-header">
                            <div>
                                <div className="dashboard-mode-title">Certification Analytics</div>
                                <div className="dashboard-mode-subtitle">Pass/fail overview and per-quiz performance for certification attempts.</div>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                            <div className="dashboard-insight-card certification-insight-card">
                                <div className="text-sm text-[#666]">Certification Quizzes</div>
                                <div className="text-3xl font-semibold text-[#1f1f1f] mt-2">{certificationSections.length}</div>
                            </div>
                            <div className="dashboard-insight-card certification-insight-card">
                                <div className="text-sm text-[#666]">Certification Attempts</div>
                                <div className="text-3xl font-semibold text-[#1f1f1f] mt-2">{certificationAttempts}</div>
                            </div>
                            <div className="dashboard-insight-card certification-insight-card">
                                <div className="text-sm text-[#666]">Pass Rate</div>
                                <div className="text-3xl font-semibold text-[#1f1f1f] mt-2">{certificationPassRate}%</div>
                            </div>
                            <div className="dashboard-insight-card certification-insight-card">
                                <div className="text-sm text-[#666]">Average Result</div>
                                <div className="text-3xl font-semibold text-[#1f1f1f] mt-2">{certificationAverageScore}%</div>
                            </div>
                        </div>

                        {certificationSections.length === 0 ? (
                            <div className="text-center py-12 text-muted-foreground">No certification quiz data available.</div>
                        ) : (
                            <div className="space-y-6">
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    <div className="dashboard-chart-card certification-chart-card">
                                        <div className="dashboard-chart-card-title">Pass vs Fail</div>
                                        <div className="dashboard-chart-card-subtitle">Distribution across all certification attempts.</div>
                                        <div className="h-[280px] flex items-center justify-center">
                                            <Pie data={certificationPieData} options={{ responsive: true, plugins: { legend: { position: 'bottom' } } }} />
                                        </div>
                                    </div>
                                    <div className="dashboard-chart-card certification-chart-card">
                                        <div className="dashboard-chart-card-title">Average Result By Quiz</div>
                                        <div className="dashboard-chart-card-subtitle">Higher bars indicate stronger certification performance.</div>
                                        <div className="h-[280px]">
                                            <Bar
                                                data={certificationBarData}
                                                options={{
                                                    responsive: true,
                                                    maintainAspectRatio: false,
                                                    plugins: { legend: { display: false } },
                                                    scales: { y: { min: 0, max: 100, title: { display: true, text: 'Result %' } } }
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                                {certificationSections.map((section) => (
                                    <div key={section.quiz_id} className="rounded-2xl border border-[#dbe4ff] overflow-hidden bg-white shadow-[0_20px_45px_rgba(79,70,229,0.08)]">
                                        <div className="px-5 py-4 border-b border-[#dde3ff] bg-[linear-gradient(135deg,#eef2ff_0%,#f8fafc_55%,#ecfeff_100%)]">
                                            <div className="text-lg font-semibold">{section.quiz_name}</div>
                                            <div className="text-sm text-[#666] mt-1">
                                                {section.topic_name}{section.subtopic_name ? ` / ${section.subtopic_name}` : ""} • Passing Mark {section.passing_percentage}%
                                            </div>
                                        </div>
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full text-sm">
                                                <thead>
                                                    <tr className="border-b border-[#ececec]">
                                                        <th className="text-left px-4 py-3">SL No.</th>
                                                        <th className="text-left px-4 py-3">Candidate</th>
                                                        <th className="text-left px-4 py-3">Conducting CLI</th>
                                                        <th className="text-left px-4 py-3">Result %</th>
                                                        <th className="text-left px-4 py-3">Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {section.rows.length === 0 ? (
                                                        <tr>
                                                            <td className="px-4 py-6 text-center text-muted-foreground" colSpan={5}>
                                                                No certification attempts for this quiz yet.
                                                            </td>
                                                        </tr>
                                                    ) : section.rows.map((row) => (
                                                        <tr key={row.id} className={`border-b border-[#f1f1f1] ${row.serial_no <= 3 ? `rank-row rank-row--${row.serial_no}` : ""}`}>
                                                            <td className="px-4 py-3 rank-col-cell">
                                                                <span className="rank-number">{MEDAL[row.serial_no] ? `${MEDAL[row.serial_no]} ${row.serial_no}` : row.serial_no}</span>
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                <div>{row.candidate_name}</div>
                                                                <div className="text-xs text-[#777]">{row.designation_hq || "—"}</div>
                                                            </td>
                                                            <td className="px-4 py-3">{row.selected_cli_name || row.cli_name || "—"}</td>
                                                            <td className="px-4 py-3">{Math.round(row.score_percentage || 0)}%</td>
                                                            <td className="px-4 py-3">
                                                                <span className={`score-badge ${row.is_passed ? "score-badge--pass" : "score-badge--fail"}`}>
                                                                    {row.is_passed ? "Passed" : "Failed"}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    )}
                    <div style={{ display: 'none' }}>
                        <div className="row g-4">
                            <div
                                className="analytics-container"
                                style={{
                                    backgroundColor: "#F1EEE5",
                                }}
                            >
                                <div className="topic-box">
                                    {topics.map((topic, index) => (
                                        <div
                                            key={index}
                                            className={`topic-item ${selectedTopic ===
                                                topic.topic_name
                                                ? "active"
                                                : ""
                                                }`}
                                            onClick={() =>
                                                handleTopicSelect(
                                                    topic.topic_name
                                                )
                                            }
                                        >
                                            {topic?.topic_name}
                                        </div>
                                    ))}
                                </div>
                                <div
                                    className="gray-line"
                                    style={{ marginLeft: "5.5%" }}
                                ></div>
                                <div className="sub-topic-box">
                                    {topics
                                        .find(
                                            (topic) =>
                                                topic?.topic_name ===
                                                selectedTopic
                                        )
                                        ?.subtopics.map((subtopic: string, index: number) => (
                                            <div
                                                key={index}
                                                className={`topic-item ${selectedSubtopic ===
                                                    subtopic
                                                    ? "active"
                                                    : ""
                                                    }`}
                                                onClick={() =>
                                                    handleSubtopicSelect(
                                                        subtopic
                                                    )
                                                }
                                                style={{
                                                    fontWeight: "400",
                                                }}
                                            >
                                                {subtopic}
                                            </div>
                                        ))}
                                </div>
                                <div
                                    className="gray-line"
                                    style={{ marginLeft: "5.5%" }}
                                ></div>
                                <div className="d-flex align-items-center justify-content-between p-3">
                                    <div className="d-flex align-items-center justify-content-center" onClick={() => {
                                        handlePieChartImage();
                                        setShowAllUsers(true);
                                        scrollToTop();
                                    }}>
                                        <div
                                            className="d-flex align-items-center justify-content-center"
                                            style={{
                                                background:
                                                    "linear-gradient(180deg,rgba(0, 0, 0, 0.1) 0%,rgba(0, 0, 0, 0.01) 50%)",
                                                padding: "2px",
                                                borderRadius: "50%",
                                            }}
                                        >
                                            <div
                                                className="d-flex align-items-center justify-content-center"
                                                style={{
                                                    backgroundColor:
                                                        "#F1EEE5",
                                                    padding: "27px",
                                                    borderRadius: "50%",
                                                }}
                                            >
                                                {pieData && (
                                                    <Pie
                                                        id="pie-chart-container"
                                                        data={pieData}
                                                        options={
                                                            PieOptions
                                                        }
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="d-flex flex-column align-items-flex-start justify-content-center">
                                        <div className="pie-summary-item">
                                            <div
                                                className="pie-marker"
                                                style={{
                                                    backgroundColor:
                                                        "#002E6E",
                                                }}
                                            ></div>
                                            Marks above 50%:{" "}
                                            <strong>
                                                {highUserData?.length}
                                            </strong>
                                        </div>
                                        <div
                                            className="gray-line"
                                            style={{
                                                marginBottom: "5px",
                                                marginTop: "5px",
                                            }}
                                        ></div>
                                        <div className="pie-summary-item">
                                            <div
                                                className="pie-marker"
                                                style={{
                                                    backgroundColor:
                                                        "#FFAC40",
                                                }}
                                            ></div>
                                            Marks below 50%:{" "}
                                            <strong>
                                                {lowUserData?.length}
                                            </strong>
                                        </div>
                                    </div>
                                </div>
                                <div className="click-text"><img style={{ width: '20px' }} src={infoIcon} alt=""></img> Click the graph to show more participants</div>
                            </div>
                        </div>

                        <div className="row g-4">
                            <div className="col-12">
                                <div
                                    className="p-3 rounded-bottom"
                                    style={{ backgroundColor: "#F1EEE5" }}
                                >
                                    <div className="d-flex justify-content-between align-items-center px-3 py-2 mb-3">
                                        <div
                                            style={{
                                                fontSize: "25px",
                                                fontWeight: "600",
                                            }}
                                        >
                                            Participants
                                        </div>
                                    </div>
                                    <div className="d-flex flex-column gap-3 small-participant-box">
                                        {filteredData?.map(
                                            (participant, index) => (
                                                <div
                                                    key={index}
                                                    className="participant-profile d-flex align-items-center p-1 rounded"
                                                >
                                                    <div
                                                        className="d-flex justify-content-center align-items-center p-2"
                                                        style={{
                                                            borderRadius: "50%",
                                                            backgroundColor: "#C1DDFF",
                                                            color: "#555",
                                                            fontWeight: "bold",
                                                            fontSize: "1rem",
                                                            width: "40px",
                                                            height: "40px"
                                                        }}
                                                    >
                                                        {participant.name
                                                            .split(" ")
                                                            .map(
                                                                (word) =>
                                                                    word[0]
                                                            )
                                                            .join("")}
                                                    </div>
                                                    <div className="w-100 d-flex align-items-center justify-content-between px-3">
                                                        <div className="participant-name">
                                                            {
                                                                participant.name
                                                            }
                                                        </div>{" "}
                                                        <span className="text-muted">
                                                            {
                                                                participant.designation
                                                            }
                                                        </span>
                                                    </div>
                                                </div>
                                            )
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;
